import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import { Button, Stack, Tooltip } from "@mui/material";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";

import MissingMaskComponent from "./MissingMaskComponent";

import { api, uuidv4 } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { PaginationModel } from "../../../../../models/ReportGridModels";
import { GetMaskUrl, MaskImageModel } from "../EditMaskStructure/EditMaskStructure";

interface MaskStructureRequest {
    data: MaskStructureData;
    error: IError;
}
interface MaskStructureData {
    masks: MaskImageModel[];
}

interface MaskImagesResponse {
    data: MaskImagesData;
    error: IError;
}

interface MaskImagesData {
    masks: string[];
}

interface SaveMaskImagesResponse {
    data: SaveMaskImagesData;
    error: IError;
}
interface SaveMaskImagesData {
    status: string;
}

const ratios: string[] = ["16x9", "4x3", "3x2", "16x9 or 4x3"];
const vehicleTypes: string[] = ["regular", "motorcycle", "van"];

const columns = [
    { field: 'id', headerName: 'ID', width: 10 },
    {
        field: 'imageName', headerName: 'Mask image', width: 500,
        renderCell: (param: any) => (
            <Tooltip
                title={
                    <Stack direction={"column"} sx={{ backgroundColor: "white" }}>
                        {param.row.imageName == "-" ?
                            <div style={{ height: "200px", minWidth: "280px", border: "1px black solid" }}>
                            </div>
                            :
                            <img
                                src={GetMaskUrl(param.row.imageName.toString())}
                                height={"200px"}
                                loading="lazy"
                            />
                        }
                    </Stack>
                }>
                <span>{param.row.imageName}</span>
            </Tooltip>
        )
    },
    { field: 'ratio', headerName: 'Ratio', width: 130, editable: true, type: "singleSelect", valueOptions: ratios },
    { field: 'vehicleType', headerName: 'Vehicle Type', width: 130, editable: true, type: "singleSelect", valueOptions: vehicleTypes },
];
const columnVisibility = {
    id: false
}

function UpdateMaskImages() {
    const { enqueueSnackbar } = useSnackbar();

    const [maskStructure, setMaskStructure] = useState<MaskImageModel[]>([]);

    const [missingMasks, setMissingMasks] = useState<string[]>([]);

    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 100,
        page: 0,
    });

    const apiRef = useGridApiRef();

    useEffect(() => {
        GetMaskStructure();
    }, [])

    const GetMaskStructure = async () => {
        setMaskStructure([])
        try {
            const response: MaskStructureRequest = await api.get("api/Admin/GetMaskStructure", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error === null) {
                setMaskStructure(response.data.masks);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetMaskImages = async () => {
        setMissingMasks([]);
        try {
            const response: MaskImagesResponse = await api.get("api/Admin/GetMaskImages", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                const missing: string[] = [];
                var allMaskImages = response.data.masks;

                for (let i = 0; i < allMaskImages.length; i++) {
                    if (maskStructure.some((obj) => { return obj.imageName == allMaskImages[i] }))
                        continue;
                    missing.push(allMaskImages[i])
                }

                setMissingMasks(missing);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const SaveMaskStructure = async () => {
        const resultArray: MaskImageModel[] = []

        const map = apiRef.current.getRowModels();
        for (const [_key, value] of map) {
            resultArray.push(value as MaskImageModel);
        }

        try {
            const response: SaveMaskImagesResponse = await api.post("api/Admin/SaveMaskImages", {
                json: resultArray,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Saved!", { variant: "success" });
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const AddFromMissing = useCallback((maskName: string) => {
        const missingIndex = missingMasks.indexOf(maskName);
        if (missingIndex == -1)
            return;
        missingMasks.splice(missingIndex, 1);
        setMissingMasks(missingMasks);

        const newElement: MaskImageModel = {
            id: uuidv4(),
            imageName: maskName,
            imagePath: GetMaskUrl(maskName.toString()),
            ratio: "16x9",
            vehicleType: "regular"
        };

        // add right after the "-" maks
        setMaskStructure(current => {
            const newArray = [
                ...current.slice(0, 1),
                newElement,
                ...current.slice(1)
            ];
            return newArray;
        });
    }, [missingMasks])

    function GetPanelHeight() {
        return window.innerHeight - 170;
    }

    return (
        <Stack>
            {/* Database names */}
            <Stack
                direction={"row"}
                alignItems={"center"}>

                <Button variant="outlined"
                    onClick={GetMaskStructure}
                    sx={{ width: "90px" }}>
                    Refresh
                </Button>
                <Button variant="outlined"
                    onClick={SaveMaskStructure}
                    sx={{ width: "50px", marginLeft: "10px" }}>
                    Save
                </Button>
            </Stack>
            <Stack direction={"row"} spacing={2}>
                <DataGrid
                    sx={{ height: GetPanelHeight() + 20, maxWidth: "800px" }}
                    apiRef={apiRef}
                    rows={maskStructure}
                    columns={columns}
                    columnVisibilityModel={columnVisibility}
                    pagination
                    pageSizeOptions={[10, 15, 20, 50, 100, 300]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    editMode="row"
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 100 },
                        },
                    }} />
                <Stack>
                    <Button variant="outlined"
                        onClick={GetMaskImages}
                        sx={{ width: "200px" }}>
                        Recheck mask images
                    </Button>
                    <Stack direction='column'
                        sx={{ width: '600px', height: GetPanelHeight() - 36, marginTop: '10px', overflowX: 'auto', overflowY: 'auto' }}>
                        {missingMasks.map((maskName: string, index) =>
                            <MissingMaskComponent
                                key={index}
                                maskName={maskName}
                                addFromMissing={AddFromMissing} />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default UpdateMaskImages;